/*
 * Header Enova (sections/enova-header.liquid).
 * - Móvil: menú por categorías que se despliega bajo la cabecera, con la altura animada al
 *   cambiar de nivel.
 * - Móvil: la fila del buscador se esconde al bajar y vuelve al subir.
 * - Campo de búsqueda (escritorio y móvil): rota por las búsquedas populares con su recuento.
 * - Escritorio: el megamenú se abre con hover (clic como respaldo).
 * - Publica la altura del header en variables que usa el resto del tema.
 */
if (!customElements.get('enova-header')) {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const nextFrames = () => new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

  // Sugerencias del campo de búsqueda: «Buscar «término»» con su recuento volado. El término
  // sale hacia arriba y el siguiente entra desde abajo; el número cuenta desde cero.
  class EnovaSearchSuggest {
    constructor(field, terms, header) {
      this.field = field;
      this.terms = terms;
      this.header = header;
      this.termEl = field.querySelector('[data-enova-suggest-term]');
      this.labelEl = field.querySelector('[data-enova-suggest-label]');
      this.countEl = field.querySelector('[data-enova-suggest-count]');
      this.index = 0;
      this.token = 0;
      this.running = false;
      this.visible = false;
    }

    init() {
      // El primer término viene pintado desde Liquid; si no tiene productos se salta.
      if (this.header.suggestCount(this.terms[0]) === 0) this.advance();
      this.labelEl.textContent = `«${this.terms[this.index]}»`;
      this.setCount();
    }

    // Corre solo si el campo se ve, la fila no está oculta, el buscador está cerrado y la
    // pestaña está al frente. fresh: al reaparecer cambia de término enseguida.
    update({ fresh = false } = {}) {
      const run =
        this.visible &&
        !document.hidden &&
        !reducedMotion.matches &&
        !this.header.classList.contains('is-search-row-hidden') &&
        this.field.getAttribute('aria-expanded') !== 'true';
      if (run && !this.running) this.start(fresh);
      else if (!run && this.running) this.stop();
    }

    start(fresh) {
      this.running = true;
      this.loop(++this.token, fresh);
    }

    stop() {
      this.running = false;
      this.token += 1;
      this.termEl.classList.remove('is-in', 'is-out');
      this.setCount();
    }

    async loop(token, fresh) {
      try {
        await this.wait(fresh ? 380 : 2300, token);
        for (;;) {
          await this.hide(token);
          this.advance();
          await this.show(token);
          await this.wait(2300, token);
        }
      } catch (error) {
        // Detenida: stop() cambió el token.
      }
    }

    wait(ms, token) {
      return new Promise((resolve) => setTimeout(resolve, ms)).then(() => {
        if (token !== this.token) throw new Error('stop');
      });
    }

    // Siguiente término con productos (los de recuento 0 se saltan, como en el buscador).
    advance() {
      for (let n = 0; n < this.terms.length; n += 1) {
        this.index = (this.index + 1) % this.terms.length;
        if (this.header.suggestCount(this.terms[this.index]) !== 0) return;
      }
    }

    async show(token) {
      const term = this.terms[this.index];
      const count = this.header.suggestCount(term);
      this.labelEl.textContent = `«${term}»`;
      this.countEl.textContent = count === undefined ? '' : '0';
      this.termEl.classList.remove('is-out');
      this.termEl.classList.add('is-in');
      await nextFrames();
      this.termEl.classList.remove('is-in');
      if (count !== undefined) await this.countUp(count, token);
      await this.wait(300, token);
    }

    async hide(token) {
      this.termEl.classList.add('is-out');
      await this.wait(300, token);
    }

    setCount() {
      const count = this.header.suggestCount(this.terms[this.index]);
      this.countEl.textContent = count === undefined || count === 0 ? '' : count;
    }

    // count puede ser «250+» (el máximo que trae el buscador): cuenta la parte numérica.
    countUp(count, token) {
      const target = parseInt(count, 10) || 0;
      return new Promise((resolve) => {
        const start = performance.now();
        const step = (now) => {
          const progress = Math.min(1, (now - start) / 700);
          if (token !== this.token || progress === 1) {
            this.countEl.textContent = count;
            resolve();
            return;
          }
          this.countEl.textContent = Math.round(target * (1 - Math.pow(1 - progress, 3)));
          requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      });
    }

    // Llegó el recuento de un término: si es el que se ve, aparece (contando si está corriendo).
    refresh(term) {
      if (term !== this.terms[this.index]) return;
      const count = this.header.suggestCount(term);
      if (count === undefined || count === 0) return;
      if (!this.running || reducedMotion.matches || this.countEl.textContent) {
        this.countEl.textContent = count;
        return;
      }
      this.countEl.textContent = '0';
      this.countUp(count, this.token);
    }
  }

  class EnovaHeader extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.shopify-section');
      this.topbar = this.querySelector('[data-enova-header-topbar]');
      this.bar = this.querySelector('[data-enova-header-bar]');
      this.burger = this.querySelector('[data-enova-header-burger]');
      this.drawer = this.querySelector('[data-enova-header-drawer]');
      this.overlay = this.querySelector('[data-enova-header-overlay]');
      this.desktopQuery = window.matchMedia('(min-width: 1024px)');

      if (this.section && this.dataset.sticky === 'always') {
        this.section.classList.add('is-sticky');
        document.documentElement.classList.add('enova-sticky-header');
      }

      this.abort = new AbortController();
      const signal = this.abort.signal;

      this.setHeights = this.setHeights.bind(this);
      this.setHeights();
      window.addEventListener('resize', this.setHeights, { signal });
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.setHeights);
        this.resizeObserver.observe(this);
      }

      this.initDrawer(signal);
      this.initPanels(signal);
      this.initSuggestions(signal);
      this.initSearchRow(signal);
      this.initTransparent(signal);
      this.initTopbar(signal);

      document.addEventListener('enova-search:open', () => {
        this.closeDrawer();
        this.closePanels();
      }, { signal });

      this.overlay?.addEventListener('click', () => {
        this.closeDrawer();
        this.closePanels();
      }, { signal });

      window.addEventListener('scroll', () => {
        if (this.classList.contains('is-menu-open') || this.classList.contains('is-panel-open')) {
          this.setDrawerTop();
        }
      }, { passive: true, signal });

      this.desktopQuery.addEventListener('change', () => {
        this.closeDrawer();
        this.closePanels();
      }, { signal });

      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        if (this.classList.contains('is-menu-open')) {
          this.closeDrawer();
          this.burger?.focus();
        }
        this.closePanels();
      }, { signal });

      if (this.hasAttribute('data-design-mode')) this.initEditor(signal);
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.resizeObserver?.disconnect();
      if (this.classList.contains('is-menu-open')) {
        document.documentElement.classList.remove('prevent-scroll');
      }
    }

    // Otras partes del tema (PDP fija, sliders a pantalla completa) leen estas variables del header original.
    setHeights() {
      const root = document.documentElement;
      const barHeight = this.bar ? this.bar.offsetHeight : 0;
      const topbarHeight = this.topbar ? this.topbar.offsetHeight : 0;
      root.style.setProperty('--m-header-height', `${barHeight}px`);
      root.style.setProperty('--m-announcement-height', `${topbarHeight}px`);
      root.style.setProperty('--enova-header-topbar-height', `${topbarHeight}px`);
      if (window.MinimogSettings) window.MinimogSettings.headerHeight = barHeight;
      if (this.classList.contains('is-menu-open')) this.setDrawerTop();
    }

    setDrawerTop() {
      const bottom = Math.max(0, this.bar.getBoundingClientRect().bottom);
      this.style.setProperty('--enova-header-drawer-top', `${bottom}px`);
    }

    /* Menú móvil --------------------------------------------------------- */
    initDrawer(signal) {
      if (!this.burger || !this.drawer) return;

      this.burger.addEventListener('click', () => {
        this.classList.contains('is-menu-open') ? this.closeDrawer() : this.openDrawer();
      }, { signal });

      this.drawer.addEventListener('click', (event) => {
        const openSub = event.target.closest('[data-enova-header-open-sub]');
        if (openSub) {
          this.showLevel(openSub.dataset.enovaHeaderOpenSub);
          return;
        }
        if (event.target.closest('[data-enova-header-back]')) {
          this.showLevel('root');
        }
      }, { signal });

      this.querySelector('.m-cart-icon-bubble')?.addEventListener('click', () => this.closeDrawer(), { signal });
    }

    openDrawer() {
      document.querySelector('enova-search-drawer')?.close();
      this.setDrawerTop();
      this.showLevel('root');
      this.classList.add('is-menu-open');
      this.syncOpenState();
      this.burger.setAttribute('aria-expanded', 'true');
      this.burger.setAttribute('aria-label', 'Cerrar menú');
      this.drawer.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('prevent-scroll');
    }

    closeDrawer() {
      if (!this.drawer || !this.classList.contains('is-menu-open')) return;
      this.resetDrawerHeight();
      this.classList.remove('is-menu-open');
      this.syncOpenState();
      this.burger.setAttribute('aria-expanded', 'false');
      this.burger.setAttribute('aria-label', 'Menú');
      this.drawer.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('prevent-scroll');
    }

    showLevel(level) {
      if (!this.drawer) return;
      const drawer = this.drawer;
      const animate =
        this.classList.contains('is-menu-open') &&
        !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      // Altura visible actual (si otra animación está en curso, parte de donde va).
      const from = animate ? drawer.getBoundingClientRect().height : 0;

      drawer.querySelectorAll('[data-enova-header-level]').forEach((node) => {
        node.classList.toggle('is-active', node.dataset.enovaHeaderLevel === String(level));
      });
      drawer.querySelectorAll('[data-enova-header-open-sub]').forEach((button) => {
        button.setAttribute('aria-expanded', String(button.dataset.enovaHeaderOpenSub === String(level)));
      });
      drawer.scrollTop = 0;

      if (animate) this.animateDrawerHeight(from);
    }

    // El menú mide lo que su contenido: al cambiar de nivel se anima de la altura anterior
    // a la nueva, en vez de saltar.
    animateDrawerHeight(from) {
      const drawer = this.drawer;
      this.resetDrawerHeight();
      const to = drawer.getBoundingClientRect().height;
      if (!from || Math.abs(to - from) < 1) return;

      drawer.style.height = `${from}px`;
      void drawer.offsetHeight;
      drawer.classList.add('is-resizing');
      drawer.style.height = `${to}px`;

      this.resizeFinish = (event) => {
        if (event && (event.target !== drawer || event.propertyName !== 'height')) return;
        this.resetDrawerHeight();
      };
      drawer.addEventListener('transitionend', this.resizeFinish);
      // Si la altura queda topada por max-height no hay transitionend: se limpia igual.
      this.resizeTimer = setTimeout(this.resizeFinish, 500);
    }

    // Quita cualquier animación de altura en curso y deja el menú a la altura de su contenido.
    resetDrawerHeight() {
      clearTimeout(this.resizeTimer);
      if (this.resizeFinish) this.drawer.removeEventListener('transitionend', this.resizeFinish);
      this.resizeFinish = null;
      this.drawer.classList.remove('is-resizing');
      this.drawer.style.height = '';
    }

    /* Barra superior con dos mensajes ------------------------------------ */
    // Alterna los mensajes con la transición del campo de búsqueda: el actual sale hacia arriba
    // (0,3 s) y el siguiente entra desde abajo (0,45 s). Se detiene con el cursor encima (para
    // poder hacer clic en su enlace) y con la pestaña en segundo plano. Con movimiento reducido
    // cambia sin animación.
    initTopbar(signal) {
      const bar = this.topbar;
      const messages = bar ? Array.from(bar.querySelectorAll('[data-enova-topbar-msg]')) : [];
      const interval = parseInt(bar?.dataset.enovaTopbarInterval, 10);
      if (messages.length < 2 || !interval) return;

      let index = 0;
      let timer = 0;
      let paused = false;
      const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

      const next = async () => {
        const current = messages[index];
        index = (index + 1) % messages.length;
        const incoming = messages[index];
        if (reducedMotion.matches) {
          current.classList.remove('is-current');
          incoming.classList.add('is-current');
        } else {
          current.classList.add('is-out');
          await sleep(300);
          current.classList.remove('is-current', 'is-out');
          incoming.classList.add('is-in', 'is-current');
          await nextFrames();
          incoming.classList.remove('is-in');
        }
        current.setAttribute('aria-hidden', 'true');
        incoming.removeAttribute('aria-hidden');
      };

      const schedule = () => {
        clearTimeout(timer);
        if (paused || document.hidden || signal.aborted) return;
        timer = setTimeout(async () => {
          await next();
          schedule();
        }, interval);
      };

      bar.addEventListener('pointerenter', () => {
        paused = true;
        clearTimeout(timer);
      }, { signal });
      bar.addEventListener('pointerleave', () => {
        paused = false;
        schedule();
      }, { signal });
      document.addEventListener('visibilitychange', schedule, { signal });
      signal.addEventListener('abort', () => clearTimeout(timer));
      schedule();
    }

    /* Cabecera transparente (inicio) ---------------------------------------- */
    // Transparente solo arriba de todo (hasta 8 px de scroll), sin el cursor encima y sin menú
    // ni megamenú abiertos. Con «Solo en escritorio», en móvil queda siempre con su fondo.
    initTransparent(signal) {
      const mode = this.dataset.transparent;
      if (!mode) return;
      let hover = false;
      const update = () => {
        const on = (mode === 'all' || this.desktopQuery.matches) &&
          window.scrollY <= 8 &&
          !hover &&
          !this.classList.contains('is-menu-open') &&
          !this.classList.contains('is-panel-open');
        if (this.classList.contains('is-transparent') !== on) this.classList.toggle('is-transparent', on);
      };
      let ticking = false;
      window.addEventListener('scroll', () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          update();
        });
      }, { passive: true, signal });
      this.bar?.addEventListener('pointerenter', (event) => {
        if (event.pointerType !== 'mouse') return;
        hover = true;
        update();
      }, { signal });
      this.bar?.addEventListener('pointerleave', (event) => {
        if (event.pointerType !== 'mouse') return;
        hover = false;
        update();
      }, { signal });
      this.desktopQuery.addEventListener('change', update, { signal });
      // Menú móvil y megamenú cambian la clase de la cabecera
      const observer = new MutationObserver(update);
      observer.observe(this, { attributes: true, attributeFilter: ['class'] });
      signal.addEventListener('abort', () => observer.disconnect());
      update();
    }

    /* Fila del buscador en móvil ---------------------------------------- */
    // Con la cabecera fija se esconde al bajar y vuelve al subir. Se ignoran los movimientos
    // de menos de 8px (rebote de iOS, scrolls mínimos) y cerca del inicio siempre se ve.
    initSearchRow(signal) {
      if (!this.querySelector('[data-enova-header-search-row]') || this.dataset.sticky !== 'always') return;

      let lastY = Math.max(0, window.scrollY);
      let ticking = false;

      const update = () => {
        ticking = false;
        const y = Math.max(0, window.scrollY);
        // Con el menú o el buscador abiertos la página está bloqueada: no se toca la fila.
        if (this.desktopQuery.matches || document.documentElement.classList.contains('prevent-scroll')) {
          lastY = y;
          return;
        }
        if (y <= this.offsetHeight) {
          this.setSearchRowHidden(false);
          lastY = y;
          return;
        }
        if (Math.abs(y - lastY) < 8) return;
        this.setSearchRowHidden(y > lastY);
        lastY = y;
      };

      window.addEventListener('scroll', () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(update);
      }, { passive: true, signal });

      this.desktopQuery.addEventListener('change', () => this.setSearchRowHidden(false), { signal });
    }

    // Al reaparecer la fila, sus sugerencias cambian de término enseguida.
    setSearchRowHidden(hidden) {
      if (this.classList.contains('is-search-row-hidden') === hidden) return;
      this.classList.toggle('is-search-row-hidden', hidden);
      this.suggests?.forEach((suggest) => suggest.update({ fresh: !hidden }));
    }

    /* Sugerencias del campo de búsqueda --------------------------------- */
    // Los recuentos los calcula el buscador (enova-search.js) con la misma búsqueda que
    // muestra al tocar el término: se leen de su guardado en la sesión y llegan nuevos con el
    // evento enova-search:term-count. Sin recuento, el término se muestra sin número.
    initSuggestions(signal) {
      const fields = Array.from(this.querySelectorAll('[data-enova-search-suggest]'));
      if (!fields.length) return;

      this.suggestCounts = this.readSuggestCounts();
      this.suggests = fields
        .map((field) => {
          let terms = [];
          try {
            terms = JSON.parse(field.dataset.enovaSearchSuggest);
          } catch (error) {
            terms = [];
          }
          return new EnovaSearchSuggest(field, terms, this);
        })
        .filter((suggest) => suggest.terms.length > 0 && suggest.termEl);
      this.suggests.forEach((suggest) => suggest.init());

      // Solo rota el campo visible (el de escritorio o la fila móvil) y mientras está en pantalla.
      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const suggest = this.suggests.find((item) => item.field === entry.target);
          if (!suggest) return;
          suggest.visible = entry.isIntersecting;
          suggest.update();
        });
      });
      // Buscador abierto o cerrado: enova-search.js cambia aria-expanded de los disparadores.
      const mo = new MutationObserver(() => this.suggests.forEach((suggest) => suggest.update()));
      this.suggests.forEach((suggest) => {
        io.observe(suggest.field);
        mo.observe(suggest.field, { attributes: true, attributeFilter: ['aria-expanded'] });
      });

      document.addEventListener('visibilitychange', () => {
        this.suggests.forEach((suggest) => suggest.update({ fresh: !document.hidden }));
      }, { signal });

      document.addEventListener('enova-search:term-count', (event) => {
        const { term, count } = event.detail || {};
        if (!term) return;
        this.suggestCounts[term] = count;
        this.suggests.forEach((suggest) => suggest.refresh(term));
      }, { signal });

      signal.addEventListener('abort', () => {
        io.disconnect();
        mo.disconnect();
        this.suggests.forEach((suggest) => suggest.stop());
      });
    }

    readSuggestCounts() {
      try {
        const saved = JSON.parse(sessionStorage.getItem('enova-search-terms')) || {};
        const counts = {};
        Object.keys(saved).forEach((term) => {
          counts[term] = saved[term].count;
        });
        return counts;
      } catch (error) {
        return {};
      }
    }

    suggestCount(term) {
      return this.suggestCounts ? this.suggestCounts[term] : undefined;
    }

    /* Megamenú de escritorio ------------------------------------------- */
    // Los ítems con submenú (SHOP) son botones: abren su panel al pasar el mouse y se cierran
    // al salir (con una pequeña espera para cruzar del ítem al panel). El clic queda como
    // respaldo para pantallas táctiles y teclado; un clic fuera o Escape lo cierran.
    initPanels(signal) {
      this.items = Array.from(this.querySelectorAll('.enova-header__nav-item--parent'));
      const canHover = window.matchMedia('(hover: hover) and (pointer: fine)');
      let closeTimer;

      this.items.forEach((item) => {
        const trigger = item.querySelector('.enova-header__nav-link');

        item.addEventListener('mouseenter', () => {
          if (!this.desktopQuery.matches || !canHover.matches) return;
          clearTimeout(closeTimer);
          this.openPanel(item);
        }, { signal });

        item.addEventListener('mouseleave', () => {
          if (!canHover.matches) return;
          clearTimeout(closeTimer);
          closeTimer = setTimeout(() => this.closePanel(item), 120);
        }, { signal });

        trigger.addEventListener('click', (event) => {
          if (!this.desktopQuery.matches) return;
          event.preventDefault();
          // Con mouse el panel ya se abrió al entrar: ese clic no lo cierra. Con toque o con
          // teclado (detail 0 = Enter/Espacio) el clic alterna.
          const toggles = !canHover.matches || event.detail === 0;
          if (item.classList.contains('is-open') && toggles) this.closePanel(item);
          else this.openPanel(item);
        }, { signal });

        item.addEventListener('focusout', (event) => {
          if (event.relatedTarget && !item.contains(event.relatedTarget)) this.closePanel(item);
        }, { signal });
      });

      document.addEventListener('click', (event) => {
        if (!this.classList.contains('is-panel-open')) return;
        if (this.items.some((item) => item.classList.contains('is-open') && item.contains(event.target))) return;
        this.closePanels();
      }, { signal });
    }

    openPanel(item) {
      this.items.forEach((other) => {
        if (other !== item) this.closePanel(other);
      });
      this.setDrawerTop();
      item.classList.add('is-open');
      item.querySelector('.enova-header__nav-link')?.setAttribute('aria-expanded', 'true');
      this.classList.add('is-panel-open');
      this.syncOpenState();
    }

    // Menú o panel abiertos: el html lo sabe por clase (oculta WhatsApp, enova.css). Evita un
    // html:has(), que recalculaba los estilos de toda la página con cada nodo insertado o mostrado.
    syncOpenState() {
      document.documentElement.classList.toggle(
        'enova-header-open',
        this.classList.contains('is-menu-open') || this.classList.contains('is-panel-open')
      );
    }

    closePanel(item) {
      item.classList.remove('is-open');
      item.querySelector('.enova-header__nav-link')?.setAttribute('aria-expanded', 'false');
      if (!this.items.some((other) => other.classList.contains('is-open'))) {
        this.classList.remove('is-panel-open');
        this.syncOpenState();
      }
    }

    closePanels() {
      (this.items || []).forEach((item) => this.closePanel(item));
      this.classList.remove('is-panel-open');
      this.syncOpenState();
    }

    /* Editor de temas: al seleccionar una imagen del menú, mostrar dónde está */
    initEditor(signal) {
      document.addEventListener('shopify:block:select', (event) => {
        if (!this.contains(event.target)) return;
        const panelItem = event.target.closest('.enova-header__nav-item--parent');
        if (panelItem && this.desktopQuery.matches) {
          this.openPanel(panelItem);
          return;
        }
        const level = event.target.closest('[data-enova-header-level]');
        if (level && !this.desktopQuery.matches) {
          if (!this.classList.contains('is-menu-open')) this.openDrawer();
          this.showLevel(level.dataset.enovaHeaderLevel);
        }
      }, { signal });

      document.addEventListener('shopify:block:deselect', () => this.closePanels(), { signal });
    }
  }

  customElements.define('enova-header', EnovaHeader);
}
